import { useCallback, useEffect, useState } from 'react';
import get from 'lodash/get';
import { useAuth } from '../../hooks/useAuth';
import { useToastMessage } from '../../hooks/useToastMessage';
import { Batch, UserRole } from '../../shared/VolunteerApplicationServiceTypes';
import { useBatchId } from '../../hooks/useBatchId';
import { getBatch } from '../../service/VolunteerApplicationService';
import { BatchInfoState } from '../../components/BatchInfo.store';
import { useLoadingIndicator } from '../../hooks/useLoadingIndicator';
import useUserSession from '../../hooks/useUserSession';

interface DateChecker {
	proposer?: {
		endDate?: boolean;
		startDate?: boolean;
	};
	approver?: {
		endDate?: boolean;
		startDate?: boolean;
	};
}

export const useFunctionaryHome = () => {
	const { mySrcmProfile } = useAuth();
	const { selectedRole } = useUserSession();
	const { batchId } = useBatchId();
	const { showToastMessage } = useToastMessage();
	const { setLoading } = useLoadingIndicator();
	const [messageToShow, setMessageToShow] = useState<
		| 'BATCH_CLOSED'
		| 'PAST_APPLICATION_UNDER_REVIEW'
		| 'APPLICATION_APPROVED'
		| 'INVALID_BATCH'
		| 'BATCH_NOT_STARTED_YET'
		| 'ALREADY_A_PRECEPTOR'
		| 'FUNCTIONARY_IS_NOT_A_PRECEPTOR'
		| { reapplyDate: string }
	>();
	const [batchDetails, setBatchDetails] = useState<Batch | undefined>();

	const processBatch = useCallback(async () => {
		try {
			setLoading(true);
			const batch = await getBatch({ value: batchId });
			BatchInfoState.batchInfo = batch;
			setBatchDetails(batch);
		} catch (e) {
			const errorCode: number | undefined = get(e, 'response.status');

			if (errorCode === 404) {
				window.location.replace('https://hfn.link/preceptor-application');
			} else {
				const errorMessage: string | undefined = get(
					e,
					'response.data.message'
				);

				if (errorMessage) {
					showToastMessage('error', errorMessage);
				}
				if (e instanceof Error) {
					showToastMessage('error', e.message);
				}
			}
		} finally {
			setLoading(false);
		}
	}, [batchId, setLoading, showToastMessage]);

	const processFunctionaryHome = useCallback(() => {
		if (
			(selectedRole === UserRole.PROPOSER || selectedRole === UserRole.ZC) &&
			(!mySrcmProfile?.is_prefect || mySrcmProfile?.prefect_status !== 'active')
		) {
			setMessageToShow('FUNCTIONARY_IS_NOT_A_PRECEPTOR');
			return;
		}
	}, [mySrcmProfile?.is_prefect, mySrcmProfile?.prefect_status, selectedRole]);

	const checkStartDate = useCallback((startDate?: string): boolean => {
		if (!startDate) return false;
		return new Date() >= new Date(startDate);
	}, []);

	const checkEndDate = useCallback((endDate?: string): boolean => {
		if (!endDate) return false;
		return new Date() <= new Date(endDate);
	}, []);

	const dateBasedFunctionaryHome = useCallback((): DateChecker => {
		const roleProposer = selectedRole === UserRole.PROPOSER;
		const roleApprover = selectedRole === UserRole.APPROVER;
		const dateChecker: DateChecker = {};
		if (roleProposer && batchDetails) {
			dateChecker.proposer = {
				endDate: checkEndDate(batchDetails?.proposerReviewEndDate),
				startDate: checkStartDate(batchDetails?.proposerReviewStartDate),
			};
		}

		if (roleApprover && batchDetails) {
			dateChecker.approver = {
				endDate: checkEndDate(batchDetails?.approverReviewEndDate),
				startDate: checkStartDate(batchDetails?.approverReviewStartDate),
			};
		}
		return dateChecker;
	}, [selectedRole, batchDetails, checkEndDate, checkStartDate]);

	useEffect(() => {
		void processFunctionaryHome();
		void dateBasedFunctionaryHome();
	}, [processFunctionaryHome, dateBasedFunctionaryHome]);

	useEffect(() => {
		void processBatch();
	}, [processBatch]);

	return { batchDetails, messageToShow, dateBasedFunctionaryHome };
};
